import React, { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import './EventTimeline.css';

const EventTimeline = () => {
  const { timeline, selectedEvent, events, selectedDisasterType } = useApp();

  // Filter events by selected disaster type
  const filteredEvents = events.filter(event => 
    selectedDisasterType === 'all' || event.disasterType === selectedDisasterType
  );

  const getStageIcon = (stage) => {
    const icons = {
      disaster_occurrence: '⚡',
      satellite_scan_initiated: '🛰️',
      ndvi_analysis_complete: '📊',
      quantity_loss_calculated: '📉',
      value_loss_assessment: '💰',
      loss_adjuster_assigned: '👤',
      loss_adjuster_review: '🔍',
      payout_processing: '⏳',
      payout_approved: '✅',
      payout_completed: '🎉',
      claim_rejected: '❌'
    };
    return icons[stage] || '📋';
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'completed';
      case 'in_progress':
        return 'in-progress';
      case 'failed':
        return 'failed';
      default:
        return 'pending';
    }
  };

  // Determine event status level for dynamic timeline
  const getEventStatusLevel = (event) => {
    if (!event) return 0;
    const statusLevels = {
      'pending': 1,
      'under_review': 3,
      'verified': 5,
      'approved': 6,
      'completed': 7,
      'rejected': -1
    };
    return statusLevels[event.status] || 1;
  };

  // Generate dynamic timeline based on selected event or latest event
  const displayTimeline = useMemo(() => {
    // If we have timeline data from API, use it
    if (timeline && timeline.length > 0) return timeline;

    // Use selected event or first event from filtered list
    const event = selectedEvent || (filteredEvents && filteredEvents[0]);
    const statusLevel = getEventStatusLevel(event);
    const eventDate = event?.dateReported || event?.createdAt || new Date().toISOString();
    const disasterType = event?.disasterType || 'Disaster';
    const disasterIcons = {
      wildfire: '🔥',
      flood: '🌊',
      drought: '🌵',
      windstorm: '💨',
      earthquake: '🌍',
      hail: '🧊'
    };
    const disasterIcon = disasterIcons[disasterType] || '⚠️';

    // Build dynamic timeline
    return [
      {
        _id: '1',
        stage: 'disaster_occurrence',
        title: `${disasterIcon} ${disasterType.charAt(0).toUpperCase() + disasterType.slice(1)} Detected`,
        description: event ? `${event.name || disasterType} reported at ${event.location?.address || 'location'}` : 'Awaiting disaster event data',
        status: event ? 'completed' : 'pending',
        completedAt: eventDate
      },
      {
        _id: '2',
        stage: 'satellite_scan_initiated',
        title: 'Satellite Imagery Acquired',
        description: event?.satelliteData?.ndviChange 
          ? `NDVI change detected: ${(event.satelliteData.ndviChange * 100).toFixed(1)}%`
          : 'Sentinel-2/Landsat imagery captured',
        status: statusLevel >= 1 ? 'completed' : 'pending',
        completedAt: event ? new Date(new Date(eventDate).getTime() + 3600000).toISOString() : null
      },
      {
        _id: '3',
        stage: 'ndvi_analysis_complete',
        title: 'Vegetation Analysis Complete',
        description: event?.lossPercentage 
          ? `Detected ${event.lossPercentage}% vegetation/crop damage`
          : 'NDVI change detection in progress',
        status: statusLevel >= 2 ? 'completed' : (statusLevel >= 1 ? 'in_progress' : 'pending'),
        completedAt: statusLevel >= 2 ? new Date(new Date(eventDate).getTime() + 7200000).toISOString() : null
      },
      {
        _id: '4',
        stage: 'quantity_loss_calculated',
        title: 'Stock Loss Assessment',
        description: event?.quantityBefore && event?.quantityCurrent
          ? `${event.quantityBefore - event.quantityCurrent} ${event.quantityUnit || 'units'} of ${event.commodity?.name || 'commodity'} lost`
          : 'Calculating inventory damage',
        status: statusLevel >= 3 ? 'completed' : (statusLevel >= 2 ? 'in_progress' : 'pending'),
        completedAt: statusLevel >= 3 ? new Date(new Date(eventDate).getTime() + 10800000).toISOString() : null
      },
      {
        _id: '5',
        stage: 'value_loss_assessment',
        title: 'Insurance Valuation',
        description: event?.estimatedLoss
          ? `Estimated loss: R${event.estimatedLoss.toLocaleString()} @ R${event.marketRate?.toLocaleString() || '0'}/unit`
          : 'Calculating monetary value',
        status: statusLevel >= 4 ? 'completed' : (statusLevel >= 3 ? 'in_progress' : 'pending'),
        completedAt: statusLevel >= 4 ? new Date(new Date(eventDate).getTime() + 14400000).toISOString() : null
      },
      {
        _id: '6',
        stage: event?.status === 'rejected' ? 'claim_rejected' : (statusLevel >= 6 ? 'payout_approved' : 'payout_processing'),
        title: event?.status === 'rejected' ? 'Claim Rejected' 
          : (statusLevel >= 7 ? 'Payout Completed' : (statusLevel >= 6 ? 'Payout Approved' : 'Payout Processing')),
        description: event?.payoutTriggered
          ? (event.status === 'completed' ? `R${event.estimatedLoss?.toLocaleString() || 0} paid to policyholder` 
            : `Threshold R${event.payoutThreshold?.toLocaleString() || 0} met - processing claim`)
          : (event?.status === 'rejected' ? 'Claim did not meet criteria' : 'Awaiting threshold verification'),
        status: statusLevel >= 7 ? 'completed' : (statusLevel >= 5 ? 'in_progress' : 'pending'),
        completedAt: statusLevel >= 7 ? event.dateVerified : null
      }
    ];
  }, [timeline, selectedEvent, events]);

  return (
    <div className="event-timeline">
      <div className="timeline-header">
        <span className="timeline-icon">⏰</span>
        <h3>Event Timeline</h3>
      </div>

      <div className="timeline-container">
        {displayTimeline.map((item, index) => (
          <div 
            key={item._id} 
            className={`timeline-item ${getStatusClass(item.status)}`}
          >
            <div className="timeline-marker">
              <span className="marker-icon">{getStageIcon(item.stage)}</span>
              {index < displayTimeline.length - 1 && <div className="timeline-line"></div>}
            </div>
            <div className="timeline-content">
              <div className="timeline-content-header">
                <h4>{item.title}</h4>
                <span className={`status-pill ${getStatusClass(item.status)}`}>
                  {item.status === 'in_progress' ? 'in progress' : item.status}
                </span>
              </div>
              <p className="timeline-description">{item.description}</p>
              {item.completedAt && (
                <span className="timeline-date">
                  {new Date(item.completedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="timeline-footer">
        <div className="powered-info">
          <span className="powered-label">🛰️ Powered by Sentinel-2 Satellite Data</span>
        </div>
      </div>
    </div>
  );
};

export default EventTimeline;
